/**
 * In-app card display via Gnosis Pay's PSE (Partner Secure Elements).
 *
 * The card number / expiry / CVV render inside iframes served by
 * api-pse-public.gnosispay.com — Florin never touches the PAN itself, and
 * the user never leaves the app (no Gnosis Pay dashboard, no wallet-connect).
 * Requires: our PSE appId, an ephemeral token from the Railway mTLS relay,
 * and the user's own session JWT.
 */
import { useEffect, useRef, useState } from "react";
import * as gp from "../lib/gnosispay/api";
import { getGpToken } from "../lib/db";
import { GNOSIS_PAY_API } from "../lib/config";
import { mountPseCardData, type PseElementHandle } from "../lib/pse";

const PSE_IFRAME_HOST = "https://api-pse-public.gnosispay.com";
// Railway relay — GP's PSE API rejects Cloudflare Worker egress (520), so
// the mTLS call runs on Railway instead (florin-pse project).
const TOKEN_URL = "https://florin-pse-production.up.railway.app/token";

/**
 * Render the PSE card-data iframes into `selector`. The container element
 * must already be in the DOM. Auth self-heals (silent SIWE) — the device's
 * wallet is the account, so "sign in again" is never the user's problem.
 */
export async function revealCardInto(
  selector: string,
  onError: (msg: string) => void,
): Promise<PseElementHandle> {
  const cards = await gp.withSession(() => gp.getCards());
  const card = cards.find((c) => c.cardToken);
  if (!card?.cardToken) throw new Error("Your card doesn't support in-app display yet.");
  const jwt = await getGpToken();
  if (!jwt) throw new Error("Could not establish a Gnosis Pay session.");
  const res = await fetch(TOKEN_URL, {
    method: "POST",
    headers: { authorization: `Bearer ${jwt}` },
    signal: AbortSignal.timeout(15_000),
  });
  if (!res.ok) throw new Error(`Card display service unavailable (HTTP ${res.status})`);
  const json = (await res.json()) as {
    ephemeralToken?: string;
    token?: string;
    data?: { ephemeralToken?: string; token?: string };
  };
  // GP has returned both a bare token object and a data-wrapped object across
  // its examples/environments. Accept either without weakening validation.
  const ephemeralToken = json.ephemeralToken ?? json.data?.ephemeralToken ?? json.token ?? json.data?.token;
  if (!ephemeralToken) throw new Error("Card display service returned no token.");

  return mountPseCardData(selector, {
    appId: GNOSIS_PAY_API.appId,
    iframeHost: PSE_IFRAME_HOST,
    cardToken: card.cardToken,
    ephemeralToken,
    gnosisPayApiAuthToken: jwt,
    onInvalidToken: () => onError("Card display session expired — tap Reveal again."),
    onError: (message: unknown) => onError(String(message ?? "Card display error")),
  });
}

export function CardDetails() {
  const [revealed, setRevealed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const frameRef = useRef<PseElementHandle | null>(null);
  const requestRef = useRef(0);

  const hide = () => {
    requestRef.current += 1;
    frameRef.current?.destroy();
    frameRef.current = null;
    setBusy(false);
    setRevealed(false);
  };

  useEffect(() => () => {
    requestRef.current += 1;
    frameRef.current?.destroy();
  }, []);

  const reveal = async () => {
    const request = ++requestRef.current;
    setBusy(true);
    setError("");
    try {
      setRevealed(true);
      // Let React paint the container div before the SDK looks it up.
      await new Promise((r) => setTimeout(r, 30));
      const frame = await revealCardInto("#pse-card-data", (message) => {
        if (requestRef.current === request) setError(message);
      });
      if (requestRef.current !== request) frame.destroy();
      else frameRef.current = frame;
    } catch (err) {
      if (requestRef.current !== request) return;
      setRevealed(false);
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      if (requestRef.current === request) setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="balance-label" style={{ marginBottom: 8 }}>
        💳 Card details
      </div>
      {!revealed ? (
        <>
          <p className="sub" style={{ marginBottom: 10 }}>
            Show your card number, expiry and security code — for online checkout, or to add the
            card to Apple Pay / Google Wallet.
          </p>
          <button className="btn-secondary" disabled={busy} onClick={() => void reveal()}>
            {busy ? "Loading…" : "Reveal card details"}
          </button>
        </>
      ) : (
        <>
          {/* Explicit height: the PSE iframe is height:100%, and a percentage
              against a min-height-only parent computes to 0px (blank box). */}
          <div
            id="pse-card-data"
            style={{ height: 190, background: "#fff", borderRadius: 8, overflow: "hidden" }}
          />
          <button className="btn-secondary" onClick={hide}>
            Hide
          </button>
        </>
      )}
      {error && <div className="notice">{error}</div>}
    </div>
  );
}
